<template>
  <!-- Overview for a brand with no ad data yet, but a Meta ad library we'd start scraping once it's tracked -->
  <div class="w-full grid" style="grid-template-columns: 320px 1fr;">
    <!-- LEFT SIDEBAR -->
    <div class="w-full flex flex-col p-3 border-r border-border-normal" style="min-height: calc(100vh - 139px)">
      <!-- Brand Logo and Name -->
      <div class="w-full flex items-center p-1 fade-in">
        <BrandAvatar :avatar-src="brand.avatar_url" :brand-name="brand.name" :width="32" :height="32" class="rounded-md" />
        <BaseText type="label" size="sm" class="text-text-muted pl-2.5">
          {{ brand.name ?? '--' }}
        </BaseText>
        <VerifiedBadgeIcon class="ml-1" />
      </div>
      <!-- Brand Description -->
      <BaseText v-if="description" type="body" size="sm" class="text-text-muted p-1 mt-1 fade-in">
        {{ description }}
      </BaseText>
      <!-- What tracking unlocks -->
      <div class="w-full flex flex-col gap-3 p-1 mt-3 fade-in">
        <BaseText type="body" size="sm" class="text-text-normal">
          Track to access:
        </BaseText>
        <div v-for="feature in trackToAccessFeatures" :key="`track-to-access-${feature.label}`" class="flex items-center gap-3">
          <component :is="feature.icon" class="text-text-subdued flex-shrink-0" />
          <BaseText type="label" size="sm" class="text-text-muted">
            {{ feature.label }}
          </BaseText>
        </div>
      </div>
    </div>
    <!-- CONTENT AREA: the previews tracking fills in, behind a prompt to track -->
    <div class="relative w-full min-w-0 px-4 pt-4 pb-16 fade-in">
      <TrackBrandPreviewsSkeleton />
      <div class="track-prompt-card absolute flex flex-col items-center bg-white">
        <div v-if="brand.is_monitored" class="flex flex-col items-center p-3 text-center">
          <CollectingBrandDataAnimation />
          <BaseText type="label" size="sm" class="text-text-muted mt-5">
            Foreplay has started collecting this brand's data
          </BaseText>
          <BaseText type="body" size="sm" class="text-text-normal mt-1.5" style="width: 440px;">
            This can take a few days. Top ranked ads, creative velocity and partnerships show up here as they come in.
          </BaseText>
          <BaseText type="body" size="sm" class="text-text-normal mt-6" style="width: 440px;">
            You can leave this page. We’ll keep collecting in the background.
          </BaseText>
        </div>
        <div v-else class="flex flex-col items-center gap-5 p-3">
          <TrackBrandIllustration />
          <div class="flex flex-col items-center text-center" style="width: 400px; gap: 5px;">
            <BaseText type="label" size="sm" class="text-text-muted">
              Track this brand to have Foreplay start collecting its data
            </BaseText>
            <BaseText type="body" size="sm" class="text-text-normal" style="width: 360px;">
              Top ranked ads, creative velocity and partnerships show up here once we have its ads.
            </BaseText>
          </div>
          <BasePrimaryButton text="Start Tracking" :icon="trackIcon" @click="trackBrand" />
        </div>
        <!-- Similar Brands: left out until they load, then the prompt grows to fit them -->
        <div v-if="similarBrands.length" class="similar-brands-reveal w-full overflow-hidden slow-fade-in" :style="{ height: similarBrandsHeight }">
          <div ref="similarBrands" class="pt-5">
            <div class="similar-brands-tray w-full flex flex-col gap-1 p-1.5 bg-neutral-25 overflow-hidden">
              <BaseText type="body" size="sm" class="text-text-normal text-center py-3">
                Similar brands
              </BaseText>
              <div class="grid grid-cols-3 gap-2">
                <router-link v-for="similarBrand in similarBrands" :key="`similar-brand-${similarBrand.id}`"
                :to="{ name: 'SpyderView', params: { id: similarBrand.id } }"
                class="similar-brand-card flex items-center gap-3 p-4 min-w-0 bg-white">
                  <BrandAvatar :avatar-src="similarBrand.avatar" :brand-name="similarBrand.name" :width="44" :height="44"
                  class="rounded-md flex-shrink-0" />
                  <div class="flex-grow flex flex-col gap-1 min-w-0">
                    <BaseText type="label" size="sm" class="text-text-muted truncate">
                      {{ similarBrand.name }}
                    </BaseText>
                    <BaseText v-if="similarBrand.domain" type="body" size="sm" class="text-text-normal truncate">
                      {{ similarBrand.domain }}
                    </BaseText>
                  </div>
                </router-link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ForeplayAPI from '@/api/foreplayServer'
import EventBus from '@/plugins/BaseNotifications/EventBus'
import { getBrandDomains } from '@/utils/spyder/brandUtils'

// Components
import TrackBrandIllustration from './TrackBrandIllustration.vue'
import TrackBrandPreviewsSkeleton from './TrackBrandPreviewsSkeleton.vue'
import CollectingBrandDataAnimation from './CollectingBrandDataAnimation.vue'
import BrandAvatar from '../../../brands/BrandAvatar.vue'

// Icons
import VerifiedBadgeIcon from '../../../globals/Icons/AgencyIcons/VerifiedBadgeIcon.vue'
import LinkWideRoundedIcon from '../../../globals/Icons/LinkWideRoundedIcon.vue'
import SocialsIcon from '../../../globals/Icons/SocialsIcon.vue'
import SpyderIcon from '../../../globals/Icons/SidebarIcons/SpyderIcon.vue'
import BrandPlatformIcon from '../../../globals/Icons/SpyderV2Icons/BrandPlatformIcon.vue'
import CategoriesIcon from '../../../globals/Icons/SpyderV2Icons/CategoriesIcon.vue'
import MonthlyTrafficIcon from '../../../globals/Icons/SpyderV2Icons/MonthlyTrafficIcon.vue'

// One row in the track prompt
const SIMILAR_BRANDS_COUNT = 3

// Sidebar sections the brand gets once it's tracked
const TRACK_TO_ACCESS_FEATURES = [
  { label: 'Monthly Traffic', icon: MonthlyTrafficIcon },
  { label: 'Socials', icon: SocialsIcon },
  { label: 'Platform', icon: BrandPlatformIcon },
  { label: 'Categories', icon: CategoriesIcon },
  { label: 'Domains', icon: LinkWideRoundedIcon }
]

// Flattens a /similar search hit into what a similar brand card renders. The hit's `brand` is the Spyder brand.
const normalizeSimilarBrand = ({ brand }) => ({
  id: brand.id,
  name: brand.name,
  avatar: brand.avatar_url ?? null,
  domain: getBrandDomains(brand)[0] ?? null
})

export default {
  name: 'TrackableBrandOverview',
  components: {
    TrackBrandIllustration,
    TrackBrandPreviewsSkeleton,
    CollectingBrandDataAnimation,
    BrandAvatar,
    VerifiedBadgeIcon
  },
  props: {
    // Loaded brand from the /brands endpoint
    brand: {
      type: Object,
      required: true
    }
  },
  data () {
    return {
      similarBrands: [],
      // Animated from 0 to the tray's height once similar brands load
      similarBrandsHeight: '0px',
      trackToAccessFeatures: TRACK_TO_ACCESS_FEATURES,
      trackIcon: SpyderIcon
    }
  },
  computed: {
    description () {
      return this.brand.enrichment?.description ?? this.brand.description
    }
  },
  mounted () {
    this.fetchSimilarBrands()
  },
  methods: {
    async fetchSimilarBrands () {
      if (!this.brand.id) return
      try {
        const results = await ForeplayAPI.Brands.findSimilarBrands(this.brand.id, { size: SIMILAR_BRANDS_COUNT })
        // Each card links to the brand's Spyder page, so hits without a Spyder brand are left out
        this.similarBrands = (results ?? [])
          .filter(result => result.brand?.id)
          .slice(0, SIMILAR_BRANDS_COUNT)
          .map(normalizeSimilarBrand)
      } catch (error) {
        console.error('Error fetching similar brands:', error)
        return
      }
      if (!this.similarBrands.length) return

      // The tray renders at 0 height, then grows to fit. Reading its height lays out that 0 first, so the change animates
      await this.$nextTick()
      const tray = this.$refs.similarBrands
      if (tray) this.similarBrandsHeight = `${tray.offsetHeight}px`
    },
    trackBrand () {
      EventBus.$emit('spyder:track-brand')
    }
  }
}
</script>

<style scoped>
/* Overlaps both preview skeletons */
.track-prompt-card {
  top: min(456px, calc(50vh - 76px));
  left: 50%;
  transform: translate(-50%, -50%);
  width: 672px;
  max-width: calc(100% - 32px);
  padding: 6px;
  border-radius: 20px;
  box-shadow: 0px 1px 2px 0px rgba(9, 25, 72, 0.13), 0px 0px 0px 1px rgba(18, 55, 105, 0.08);
  z-index: 10;
}
.similar-brands-reveal {
  transition: height 300ms ease-in-out;
}
.similar-brands-tray {
  border-radius: 20px;
}
.similar-brand-card {
  border-radius: 12px;
  box-shadow: 0px 6px 12px -3px rgba(26, 35, 66, 0.04), 0px 2px 4px -1.5px rgba(26, 35, 66, 0.04), 0px 1px 2px -0.75px rgba(26, 35, 66, 0.04);
  transition: border-color 100ms ease-in-out;
  border: 1px solid transparent;
}
.similar-brand-card:hover {
  border-color: #DFE1E6;
}

.fade-in {
  animation: fadeIn 150ms ease-in-out;
}
.slow-fade-in {
  animation: fadeIn 300ms ease-in-out;
}
@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}
</style>
